<script setup>
import {onMounted, ref, provide, computed} from "vue";
import {useCurrencyPricesStore} from "@/stores/currencyPrices.js";
import {useUserStore} from "@/stores/user.js";
import {showMessage} from "@/utils/showMessage.js";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import {Icon} from "@iconify/vue";
import {gregorianDate} from "@/utils/gregorianDate.js";
import {Button} from "primevue";
import showAlert from "@/utils/showAlert.js";
import Breadcrumbs from "@/components/Breadcrumbs/index.vue";
import SetPrice from "@/views/stocks/currencyPricing/Add.vue";
import SortIcon from "@/components/datatable/sortIcon.vue";

const currencyPricesStore = useCurrencyPricesStore();
const userStore = useUserStore();
const loading = ref(false)
const deleting = ref(false)
const currencyPrices = ref(null)
const isShowSetPriceModal = ref(false)
const hasModifyAccess = computed(() => userStore.hasPermission("CURRENCY_PRICING_MODIFY") || userStore.hasPermission('BIG_BOSS'))
const selectedRecord = ref(null)
const fetchInitialData = async (refresh) => {
  try {
    if (!refresh) loading.value = true
    const res = await currencyPricesStore.action_getPrices();
    if (res.result === true) {
      currencyPrices.value = res.data
    } else {
      showMessage(res, 'error')
    }
  } catch (err) {
    showMessage(err, 'error')
  } finally {
    loading.value = false
  }
}

const removePrice = async (data) => {
  const ok = await showAlert(`You are deleting Price (${data.game} - ${data.name})`, 'Are you sure?', 'Yes', 'No')
  if (!ok) return

  try {
    deleting.value = true
    const res = await currencyPricesStore.action_removePrice({
      "game": data.game,
      "name": data.name
    })
    if (res.result === true) {
      showMessage('price deleted!', 'success')
      await fetchInitialData(true)
    } else {
      showMessage(res, 'error')
    }
  } catch (err) {
    showMessage(err, 'error')
  } finally {
    deleting.value = false
  }
}

function showSetPriceModal({data}) {
  if (!hasModifyAccess.value) return
  if (data) {
    selectedRecord.value = data
  } else {
    selectedRecord.value = null
  }
  isShowSetPriceModal.value = true
}

onMounted(() => {
  fetchInitialData()
})

provide('isShowSetPriceModal', isShowSetPriceModal)
</script>

<template>
  <div class="flex flex-col gap-4 w-full h-full">
    <div class="flex flex-row justify-between gap-4 w-full">
      <Breadcrumbs/>
      <div v-if="hasModifyAccess">
        <button class="btn btn-outline-dark py-1 text-xs font-medium rounded-md" @click="showSetPriceModal">Add New
        </button>
      </div>
    </div>
    <div v-if="loading" class="flex items-center justify-center h-60">
      <Icon icon="eos-icons:bubble-loading" class="size-12"/>
    </div>
    <DataTable v-else
               :nullSortOrder="-1"
               :value="currencyPrices"
               scrollable
               scrollHeight="flex"
               :virtualScrollerOptions="{ itemSize: 30 }"
               resizableColumns
               removableSort
               stripedRows
               class="text-xs w-full"
               size="small"
               tableStyle="width: 100%; table-layout: fixed;"
               row-hover
               @rowClick="showSetPriceModal"
               :rowClass="() => 'hover:!bg-[#2c4967] even:!bg-[#1A2941] odd:!bg-[#121E31] !text-slate-300'"
    >
      <Column field="name" header="Name" :sortable="true"
              class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
              style="width: 10%; min-width: 70px; height: 30px">
        <template #sorticon="{ sortOrder }">
          <SortIcon :sortOrder="sortOrder"/>
        </template>
      </Column>
      <Column field="price" header="Price" dataType="numeric" :sortable="true"
              class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
              style="width: 10%; min-width: 100px; height: 30px">
        <template #sorticon="{ sortOrder }">
          <SortIcon :sortOrder="sortOrder"/>
        </template>
      </Column>
      <Column field="lastUpdate" header="Last Update" dataType="numeric" :sortable="true"
              class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
              style="width: 8%; min-width: 100px; height: 30px">
        <template #sorticon="{ sortOrder }">
          <SortIcon :sortOrder="sortOrder"/>
        </template>
        <template #body="slotProps">
          <span class="block w-full max-w-max">
            {{ gregorianDate(slotProps.data.lastUpdate, 'DD MMM HH:mm') }}
          </span>
        </template>
      </Column>
      <Column :exportable="false"
              style="width: 5%; min-width: 70px; height: 30px" v-if="hasModifyAccess">
        <template #body="slotProps">
          <div class="flex flex-row gap-2 justify-end items-center w-full">
            <button @click.stop="removePrice(slotProps.data)">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                   class="lucide lucide-trash2-icon lucide-trash-2">
                <path d="M10 11v6"/>
                <path d="M14 11v6"/>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/>
                <path d="M3 6h18"/>
                <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
              </svg>
            </button>
          </div>
        </template>
      </Column>
    </DataTable>
    <SetPrice v-if="isShowSetPriceModal" @refresh="fetchInitialData" :selected-record="selectedRecord"/>
  </div>
</template>